import { useMemo } from 'react';

import { Avatar, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Assignment, PeopleAlt, Reply } from '@material-ui/icons';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import MicIcon from '@material-ui/icons/Mic';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import VideocamIcon from '@material-ui/icons/Videocam';

import { ReactComponent as InteractiveIcon } from 'assets/images/add-interactive-message.svg';
import CardItem from 'components/CardItem';
import { useSearchQuery } from 'contexts/SearchQueryContext';
import { useNavigateToMessage } from 'hooks/useNavigateToMessage';
import { useAppSelector } from 'store';
import { atendimentosSelector } from 'store/reducers/atendimentos';
import { MensagemInterface, TiposMensagemEnum } from 'store/reducers/mensagens';
import { getMentionColors } from 'utils/mentionColors';
import { renderTextWithMentions } from 'utils/renderTextWithMentions';

type ResponseMessageProps = {
  mensagem: MensagemInterface;
  mensagemCitada: MensagemInterface;
  fromMe?: boolean;
  isGroup?: boolean;
};

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    marginBottom: spacing(1),
  },
  dialogContent: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  titulo: {
    color: palette.text.primary,
  },
  texto: {
    display: '-webkit-box',
    maxHeight: '60px',
    overflow: 'hidden',
    lineHeight: '20px',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    color: palette.text.secondary,
    '& a.mention': {
      borderRadius: 4,
      ...getMentionColors(palette),
      paddingInline: 4,
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'inherit',
      pointerEvents: 'none',
    },
  },
}));

export default function NewResponseMessage(props: ResponseMessageProps) {
  const {
    mensagem: {
      mensagemCitada: { id },
    },
    mensagemCitada,
    fromMe,
    isGroup,
  } = props;
  const classes = useStyles({ fromMe });
  const {
    handleNavigateToMessage,
    handleNavigateToGroupMessage,
    handleNavegarAteMensagemHistorico,
  } = useNavigateToMessage();

  const { atendimentoSelecionado, historicoAtendimentoSelecionado } =
    useAppSelector(atendimentosSelector);
  const searchQuery = useSearchQuery();

  const renderIconTipoMensagem = useMemo(() => {
    switch (mensagemCitada.tipo) {
      case TiposMensagemEnum.IMAGEM:
        return <ImageIcon />;
      case TiposMensagemEnum.VIDEO:
        return <VideocamIcon />;
      case TiposMensagemEnum.CONTATO:
        return <PersonIcon />;
      case TiposMensagemEnum.AUDIO:
        return <MicIcon />;
      case TiposMensagemEnum.DOCUMENTO:
        return <InsertDriveFileIcon />;
      case TiposMensagemEnum.LOCALIZACAO:
        return <RoomIcon />;
      case TiposMensagemEnum.WPP_CTA:
        return <InteractiveIcon />;
      case TiposMensagemEnum.TEMPLATE_WHATSAPP:
        return <Assignment />;
      case TiposMensagemEnum.ARRAY_CONTATOS:
        return <PeopleAlt />;
      case TiposMensagemEnum.WPP_LIST:
        return <FormatListBulletedIcon />;
      case TiposMensagemEnum.WPP_REPLY_BTN:
        return <Reply />;
      default:
        return null;
    }
  }, [mensagemCitada.tipo]);

  const mensagensInterativas: Partial<Record<TiposMensagemEnum, string>> = {
    [TiposMensagemEnum.WPP_CTA]: 'Mensagem de CTA',
    [TiposMensagemEnum.WPP_REPLY_BTN]: 'Botão interativo',
    [TiposMensagemEnum.WPP_REPLY_BTN_RESP]: 'Resposta de botão interativo',
    [TiposMensagemEnum.WPP_LIST]: 'Lista interativa',
    [TiposMensagemEnum.WPP_LIST_RESP]: 'Resposta de lista interativa',
    [TiposMensagemEnum.TEMPLATE_WHATSAPP]: 'Mensagem de template',
    [TiposMensagemEnum.ARRAY_CONTATOS]: 'Contatos',
  };

  return (
    <CardItem
      className={classes.root}
      isQuote
      fromMe={fromMe}
      onSelect={() => {
        if (isGroup) {
          handleNavigateToGroupMessage(id);
          return;
        }

        if (historicoAtendimentoSelecionado) {
          handleNavegarAteMensagemHistorico(id);
          return;
        }

        handleNavigateToMessage(id, atendimentoSelecionado?.id);
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" flexDirection="column">
          <Typography variant="caption" className={classes.titulo}>
            VER MENSAGEM RESPONDIDA
          </Typography>

          <Typography
            variant="caption"
            className={classes.texto}
            dangerouslySetInnerHTML={{
              __html: renderTextWithMentions(
                (mensagemCitada.tipo === TiposMensagemEnum.TEXTO
                  ? mensagemCitada.mensagem
                  : mensagensInterativas[mensagemCitada.tipo] ||
                    mensagemCitada?.tipo.toLowerCase()) || '',
                mensagemCitada.contatosMencionados || [],
                searchQuery,
              ),
            }}
          />
        </Box>

        {mensagemCitada.tipo !== TiposMensagemEnum.TEXTO && (
          <Box ml={4}>
            <Avatar src={mensagemCitada?.midiaUrl}>
              {renderIconTipoMensagem}
            </Avatar>
          </Box>
        )}
      </Box>
    </CardItem>
  );
}
